import React, { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./styles.css";
import Button from "../../component/button/Button";
import {
  bulkEpisodeUploadApi,
  bulkZipUploadApi,
  getBulkEpisodeTemplateApi,
  getBulkEpisodeZipTemplateApi,
  getBulkUploadProgressApi,
  getBulkUploadTasksApi,
} from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import Loader from "../../utils/loader/Loader";

const POLL_INTERVAL_MS = 2500;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getTaskFileName = (task) =>
  task?.original_data?.video_file_name ||
  task?.original_data?.title ||
  `Row ${task?.row_number ?? "?"}`;

const ZipUploadResultModal = ({ result, onClose }) => {
  if (!result) return null;

  const { progress, completed, skipped, failed } = result;
  const noValidVideos =
    progress?.total_records === 0 && progress?.status === "completed";
  const zipParseFailed =
    progress?.status === "failed" && progress?.total_records === 0;

  return (
    <div className="zip-result-overlay">
      <div className="zip-result-modal">
        <h2>ZIP Upload Results</h2>

        {noValidVideos && (
          <div className="zip-result-warning">
            No valid video files found in zip. Use .mp4, .mov, etc. at zip root or
            in a <code>videos/</code> folder.
          </div>
        )}

        {zipParseFailed && progress?.processing_notes && (
          <div className="zip-result-error">
            {progress.processing_notes}
          </div>
        )}

        {!noValidVideos && !zipParseFailed && (
          <div className="zip-result-summary">
            {progress?.successful_uploads > 0 && (
              <p className="zip-result-success">
                {progress.successful_uploads} episode(s) uploaded successfully.
              </p>
            )}
            {skipped.length > 0 && (
              <p className="zip-result-skipped">
                {skipped.length} episode(s) skipped — already exist in this season.
              </p>
            )}
            {failed.length > 0 && (
              <p className="zip-result-failed-count">
                {failed.length} episode(s) failed to upload.
              </p>
            )}
          </div>
        )}

        {skipped.length > 0 && (
          <div className="zip-result-section">
            <h3>Skipped</h3>
            <ul className="zip-result-list">
              {skipped.map((task) => (
                <li key={task.id}>{getTaskFileName(task)}</li>
              ))}
            </ul>
          </div>
        )}

        {failed.length > 0 && (
          <div className="zip-result-section">
            <h3>Failed</h3>
            <ul className="zip-result-list zip-result-list--errors">
              {failed.map((task) => (
                <li key={task.id}>
                  <span className="zip-result-file">{getTaskFileName(task)}</span>
                  {task.error_message && (
                    <span className="zip-result-error-msg">{task.error_message}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="zip-result-actions">
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

const UploadSection = ({
  title,
  description,
  accept,
  onUpload,
  showDownload = false,
  onDownload,
  downloadLabel = "Download sample CSV",
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleFiles = (files) => {
    if (files && files.length > 0) setSelectedFile(files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleRemove = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast("Please select a file", "error");
      return;
    }
    setIsUploading(true);
    try {
      await onUpload(selectedFile);
      handleRemove();
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <h2>{title}</h2>
      <p>{description}</p>

      {showDownload && (
        <p style={{ fontSize: "0.85rem", color: "#888" }}>
          Not sure about the format?{" "}
          <span
            onClick={onDownload}
            style={{ color: "#B87333", cursor: "pointer", textDecoration: "underline" }}
          >
            {downloadLabel}
          </span>
        </p>
      )}

      <div
        className={`drop-zone ${isDragging ? "drop-zone--active" : ""}`}
        onClick={() => fileInputRef.current.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragging(false)}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={(e) => handleFiles(e.target.files)}
          hidden
        />
        <span>
          {selectedFile ? selectedFile.name : "Drag & Drop Your File Or Click To Select"}
        </span>
      </div>

      <div className="button-row">
        <Button type="button" onClick={handleUpload} disabled={isUploading}>
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
        {selectedFile && (
          <Button type="button" onClick={handleRemove}>
            Remove
          </Button>
        )}
      </div>
    </div>
  );
};

const BulkEpisode = () => {
  const { seriesId, seasonId } = useParams();
  const navigate = useNavigate();
  const [isZipLoading, setIsZipLoading] = useState(false);
  const [zipProgress, setZipProgress] = useState(null);
  const [zipResult, setZipResult] = useState(null);

  const handleDownloadFormat = async () => {
    try {
      const response = await getBulkEpisodeTemplateApi();
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "sample.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download template error:", error);
      showToast(error?.response?.data?.message || "Failed to download sample CSV", "error");
    }
  };

  const handleDownloadZipFormat = async () => {
    try {
      const response = await getBulkEpisodeZipTemplateApi();
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "episode-zip-template.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download zip template error:", error);
      showToast(error?.response?.data?.message || "Failed to download sample ZIP", "error");
    }
  };

  const handleCsvUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      await bulkEpisodeUploadApi(seriesId, seasonId, formData);
      showToast("Episodes uploaded successfully.", "success");
      navigate(`/mainlayout/series/${seriesId}/seasons/${seasonId}/episodes`);
    } catch (err) {
      console.error("Upload error:", err);
      showToast(
        err?.response?.data?.message || err.message || "Something went wrong during upload.",
        "error"
      );
    }
  };

  const pollBulkUploadProgress = async (bulkUploadId) => {
    let progress;
    const terminalStatuses = ["completed", "failed", "cancelled"];

    const fetchProgress = async () => {
      const response = await getBulkUploadProgressApi(bulkUploadId);
      return response?.data?.data;
    };

    progress = await fetchProgress();
    setZipProgress(progress);

    while (
      progress?.should_continue_polling &&
      !terminalStatuses.includes(progress?.status)
    ) {
      await sleep(POLL_INTERVAL_MS);
      progress = await fetchProgress();
      setZipProgress(progress);
    }

    return progress;
  };

  const handleZipUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      setIsZipLoading(true);
      setZipProgress(null);
      setZipResult(null);

      const zipResponse = await bulkZipUploadApi(seriesId, seasonId, formData);
      const bulkUploadId = zipResponse?.data?.data?.id;

      if (!bulkUploadId) {
        showToast("Upload started but no tracking ID was returned.", "error");
        return;
      }

      const progress = await pollBulkUploadProgress(bulkUploadId);

      const tasksResponse = await getBulkUploadTasksApi(bulkUploadId);
      const allTasks = tasksResponse?.data?.data || [];
      const completed = allTasks.filter((t) => t.status === "completed");
      const skipped = allTasks.filter((t) => t.status === "skipped");
      const failed = allTasks.filter((t) => t.status === "failed");

      setZipResult({ progress, completed, skipped, failed });
    } catch (err) {
      console.error("ZIP upload error:", err);
      showToast(
        err?.response?.data?.message || err.message || "ZIP upload failed.",
        "error"
      );
    } finally {
      setIsZipLoading(false);
      setZipProgress(null);
    }
  };

  const handleResultClose = () => {
    setZipResult(null);
    navigate(`/mainlayout/series/${seriesId}/seasons/${seasonId}/episodes`);
  };

  const zipProgressPercent = zipProgress?.progress_percentage ?? 0;
  const isProcessing = zipProgress?.status === "processing" || zipProgress?.status === "queued";

  return (
    <div className="bulk-episode">
      {isZipLoading && (
        <div className="zip-progress-overlay">
          <div className="zip-progress-content">
            <Loader />
            <p className="zip-progress-text">
              {isProcessing
                ? `Processing ZIP… ${zipProgressPercent}%`
                : "Uploading ZIP, please wait…"}
            </p>
            {isProcessing && (
              <div className="zip-progress-bar-track">
                <div
                  className="zip-progress-bar-fill"
                  style={{ width: `${zipProgressPercent}%` }}
                />
              </div>
            )}
            {zipProgress?.processing_notes && (
              <p className="zip-progress-notes">{zipProgress.processing_notes}</p>
            )}
          </div>
        </div>
      )}

      {zipResult && (
        <ZipUploadResultModal result={zipResult} onClose={handleResultClose} />
      )}

      <UploadSection
        title="Add Bulk Episodes"
        description="Upload files containing episode information"
        accept=".csv,.xls,.xlsx"
        onUpload={handleCsvUpload}
        showDownload
        onDownload={handleDownloadFormat}
      />
      <div style={{ marginTop: "40px" }}>
        <UploadSection
          title="Add Zip Files"
           description="Upload a ZIP of video files. Place .mp4, .mov, .avi, .mkv, .wmv, .flv, or .webm files at the zip root or in a videos/ folder. Optional thumbnails/ folder with matching .jpg names. Filename format: 01 - Pilot.mp4 → episode 1, title “Pilot”. Re-uploading skips existing episodes."
          accept=".zip"
          onUpload={handleZipUpload}
          showDownload
          onDownload={handleDownloadZipFormat}
          downloadLabel="Download ZIP template"
        />
      </div>
    </div>
  );
};

export default BulkEpisode;